<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { i18nInjectionKey } from '../internal';
// Imported by path (not through the barrels) for the same reason NxTable does
// it: this file is reachable from ui/src/index.ts, so the barrels would close
// an import cycle.
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxNativeSelect from '../Inputs/NxNativeSelect.vue';
import type { NxNativeSelectOption } from '../Inputs/NxNativeSelect.vue';

/**
 * Classic page-number footer for `NxTable` (`paginationMode="pages"`).
 *
 * Purely presentational: it renders prev / next, "page x of y", the row range
 * and an optional page-size select, and reports the user's intent through
 * events. NxTable owns the pagination state and turns the events into the
 * regular `onTableStateChange({ pagination: { limit, offset } })` call.
 */
const props = withDefaults(
  defineProps<{
    /** Current page, 1-based. */
    page: number;
    /** Rows per page (the table's pagination limit). */
    pageSize: number;
    /** Number of rows on the current page — drives the "x–y of z" range and the unknown-total heuristic. */
    pageRowCount: number;
    /**
     * Total number of rows across all pages. When unknown, the pager shows the
     * page number without a total and enables "next" as long as the current page
     * came back full.
     */
    total?: number;
    /** Selectable page sizes. Omit (or pass an empty list) to hide the select. */
    pageSizes?: number[];
    /** Disables all controls, e.g. while a page is loading. */
    disabled?: boolean;
  }>(),
  { total: undefined, pageSizes: () => [], disabled: false }
);

const emit = defineEmits<{
  /** The user asked for another page (1-based). */
  'change-page': [page: number];
  /** The user picked a different page size. */
  'change-page-size': [pageSize: number];
}>();

const i18nInjection = inject(i18nInjectionKey)!;

const pageCount = computed<number | undefined>(() =>
  props.total === undefined ? undefined : Math.max(1, Math.ceil(props.total / props.pageSize))
);
const hasPrev = computed(() => props.page > 1);
const hasNext = computed(() =>
  pageCount.value === undefined
    ? props.pageRowCount >= props.pageSize
    : props.page < pageCount.value
);
const rangeFrom = computed(() =>
  props.pageRowCount === 0 ? 0 : (props.page - 1) * props.pageSize + 1
);
const rangeTo = computed(() => (props.page - 1) * props.pageSize + props.pageRowCount);

const pageSizeOptions = computed<NxNativeSelectOption<number>[]>(() =>
  // The current size stays selectable even if it is not part of the offered list.
  [...new Set([...props.pageSizes, props.pageSize])]
    .sort((a, b) => a - b)
    .map((size) => ({ value: size, label: String(size) }))
);

function onPageSizeChange(value: string | number | null) {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0 || size === props.pageSize) return;
  emit('change-page-size', size);
}
</script>

<template>
  <div class="pager" role="navigation" :aria-label="i18nInjection.t('common.table.pager.label')">
    <div v-if="pageSizes.length > 0" class="pager-size">
      <span class="pager-muted">{{ i18nInjection.t('common.table.pager.page_size') }}</span>
      <NxNativeSelect
        :model-value="pageSize"
        :options="pageSizeOptions"
        :disabled="disabled"
        size="compact"
        :aria-label="i18nInjection.t('common.table.pager.page_size')"
        @update:model-value="onPageSizeChange"
      />
    </div>
    <div class="pager-nav">
      <span class="pager-muted pager-range">
        {{
          total === undefined
            ? i18nInjection.t('common.table.pager.range_unknown_total', {
                from: rangeFrom,
                to: rangeTo,
              })
            : i18nInjection.t('common.table.pager.range', { from: rangeFrom, to: rangeTo, total })
        }}
      </span>
      <NxIconButton
        height="xs"
        class="pager-button"
        data-nx-table-pager="prev"
        :disabled="disabled || !hasPrev"
        :title="i18nInjection.t('common.table.pager.prev')"
        @click="emit('change-page', page - 1)"
      >
        <ChevronLeftIcon class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <span class="pager-page" aria-live="polite">
        {{
          pageCount === undefined
            ? i18nInjection.t('common.table.pager.page_unknown_total', { page })
            : i18nInjection.t('common.table.pager.page', { page, pages: pageCount })
        }}
      </span>
      <NxIconButton
        height="xs"
        class="pager-button"
        data-nx-table-pager="next"
        :disabled="disabled || !hasNext"
        :title="i18nInjection.t('common.table.pager.next')"
        @click="emit('change-page', page + 1)"
      >
        <ChevronRightIcon class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </div>
  </div>
</template>

<style scoped>
.pager {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--nxt-space-sm) var(--nxt-space-md);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-table--fg, var(--nxt-fg));
}
.pager-size,
.pager-nav {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.pager-size {
  margin-inline-end: auto;
}
.pager-muted {
  color: var(--nxt-table--fg-muted, var(--nxt-fg-muted));
}
.pager-range,
.pager-page {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
